/**
 * Reader for CMMD's unioned environment picker payload.
 *
 * The server already fails closed; this reader fails closed a second time on
 * the client: a row that is not recognizably one of the three kinds is DROPPED
 * rather than coerced, and every field is type-checked before it is trusted.
 * A malformed payload therefore renders as a shorter list, never as a lying
 * row.
 */

import type {
  CmmdCloudSpriteEnvironmentEntry,
  CmmdDesktopEnvironmentEntry,
  CmmdEnvironmentKind,
  CmmdEnvironmentPickerEntry,
  CmmdLocalEnvironmentEntry,
} from "./cmmdEnvironmentListTypes";

/** The single string reader: an unknown field becomes the string it holds or
 * null. Used for both required fields (whose null drops the row) and optional
 * ones (whose null passes through), so the intent lives at the call site. */
function readStringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function readBoolean(value: unknown): boolean {
  return value === true;
}

function readNullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readStringArray(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function readKind(value: unknown): CmmdEnvironmentKind | null {
  return value === "cloud-sprite" || value === "desktop" || value === "local" ? value : null;
}

/** The fields every kind shares. `id` and `label` come back null-checked so a
 * row missing either is dropped before a variant is built. */
function readBaseFields(raw: Record<string, unknown>): {
  readonly id: string;
  readonly label: string;
  readonly status: string | null;
  readonly online: boolean;
  readonly lastSeenAt: string | null;
  readonly capabilities: readonly string[];
} | null {
  const id = readStringOrNull(raw["id"]);
  const label = readStringOrNull(raw["label"]);
  if (id === null || label === null) return null;
  return {
    id,
    label,
    status: readStringOrNull(raw["status"]),
    online: readBoolean(raw["online"]),
    lastSeenAt: readStringOrNull(raw["lastSeenAt"]),
    capabilities: readStringArray(raw["capabilities"]),
  };
}

function readCloudSpriteEntry(
  raw: Record<string, unknown>,
  base: NonNullable<ReturnType<typeof readBaseFields>>,
): CmmdCloudSpriteEnvironmentEntry {
  return {
    kind: "cloud-sprite",
    ...base,
    environmentId: readStringOrNull(raw["environmentId"]),
    spriteName: readStringOrNull(raw["spriteName"]),
    spriteId: readStringOrNull(raw["spriteId"]),
    icon: readStringOrNull(raw["icon"]),
    iconUrl: readStringOrNull(raw["iconUrl"]),
    lastActivityAt: readStringOrNull(raw["lastActivityAt"]),
  };
}

function readMachineEntry(
  kind: "desktop" | "local",
  raw: Record<string, unknown>,
  base: NonNullable<ReturnType<typeof readBaseFields>>,
): CmmdDesktopEnvironmentEntry | CmmdLocalEnvironmentEntry {
  const deviceId = readNullableNumber(raw["deviceId"]);
  return kind === "desktop"
    ? { kind: "desktop", ...base, deviceId }
    : { kind: "local", ...base, deviceId };
}

/**
 * One row. Returns null for anything that is not recognizably one of the three
 * kinds with an id and a label — the two fields a row cannot render without.
 */
export function readEnvironmentPickerEntry(raw: unknown): CmmdEnvironmentPickerEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const kind = readKind(record["kind"]);
  if (kind === null) return null;
  const base = readBaseFields(record);
  if (base === null) return null;
  if (kind === "cloud-sprite") return readCloudSpriteEntry(record, base);
  return readMachineEntry(kind, record, base);
}

/** The `{ environments: [...] }` payload, or an empty list when it is not. */
export function readEnvironmentListPayload(raw: unknown): readonly CmmdEnvironmentPickerEntry[] {
  if (typeof raw !== "object" || raw === null) return [];
  const record = raw as Record<string, unknown>;
  const list = record["environments"];
  if (!Array.isArray(list)) return [];
  return list
    .map(readEnvironmentPickerEntry)
    .filter((entry): entry is CmmdEnvironmentPickerEntry => entry !== null);
}
